import { useEffect, useRef } from "react";
import { API, getStaffToken, getTechToken } from "./api";

// One shared WebSocket per token kind ("staff" | "tech"), ref-counted by subscribers.
const conns = {};

function open(kind) {
  const c = conns[kind];
  if (!c || c.ws || c.subs.size === 0) return;
  const token = kind === "tech" ? getTechToken() : getStaffToken();
  if (!token) return;
  const url = API.replace(/^http/, "ws") + `/ws?token=${encodeURIComponent(token)}`;
  let ws;
  try {
    ws = new WebSocket(url);
  } catch {
    return;
  }
  c.ws = ws;
  ws.onopen = () => {
    c.retry = 0;
    c.ping = setInterval(() => {
      try { ws.send("ping"); } catch { /* noop */ }
    }, 25000);
  };
  ws.onmessage = (e) => {
    let evt;
    try { evt = JSON.parse(e.data); } catch { return; }
    if (!evt || evt.type === "pong") return;
    c.subs.forEach((h) => {
      try { h(evt); } catch { /* noop */ }
    });
  };
  ws.onclose = () => {
    clearInterval(c.ping);
    c.ws = null;
    if (c.subs.size > 0) {
      const delay = Math.min(15000, 1000 * 2 ** c.retry++);
      c.timer = setTimeout(() => open(kind), delay);
    }
  };
  ws.onerror = () => {
    try { ws.close(); } catch { /* noop */ }
  };
}

function shutdown(kind) {
  const c = conns[kind];
  if (!c) return;
  clearTimeout(c.timer);
  clearInterval(c.ping);
  if (c.ws) {
    const w = c.ws;
    c.ws = null;
    try { w.onclose = null; w.close(); } catch { /* noop */ }
  }
  c.retry = 0;
}

export function subscribeRealtime(kind, handler) {
  if (!conns[kind]) conns[kind] = { subs: new Set(), ws: null, retry: 0, timer: null, ping: null };
  const c = conns[kind];
  c.subs.add(handler);
  open(kind);
  return () => {
    c.subs.delete(handler);
    if (c.subs.size === 0) shutdown(kind);
  };
}

// React hook: call `onEvent` (debounced 300ms) when an event type matches one of the prefixes.
// kind: "staff" | "tech" — prefixes: e.g. ["job.", "payment."]
export function useRealtime(kind, prefixes, onEvent) {
  const ref = useRef(onEvent);
  ref.current = onEvent;
  const key = prefixes.join(",");
  useEffect(() => {
    let t = null;
    const parts = key.split(",");
    const unsub = subscribeRealtime(kind, (evt) => {
      if (!parts.some((p) => (evt.type || "").startsWith(p))) return;
      clearTimeout(t);
      t = setTimeout(() => ref.current(evt), 300);
    });
    return () => { clearTimeout(t); unsub(); };
  }, [kind, key]);
}
